import { useState, useEffect } from 'react';
import { Post } from '@/lib/supabase';

interface PostEditorProps {
  post: Partial<Post> | null;
  onSave: (data: Partial<Post>) => Promise<void>;
  onCancel: () => void;
  saving: boolean;
}

const categories = [
  { id: 'web', label: 'WEB制作' },
  { id: 'sns', label: 'SNS運用' },
  { id: 'seo', label: 'SEO・MEO' },
  { id: 'marketing', label: 'マーケティング' },
  { id: 'ai', label: 'AI活用' },
];

function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim()
    .slice(0, 60) || `post-${Date.now()}`;
}

export default function PostEditor({ post, onSave, onCancel, saving }: PostEditorProps) {
  const [form, setForm] = useState<Partial<Post>>({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: 'web',
    tags: [],
    cover_image: '',
    author_name: '後藤慎吾',
    author_role: 'GRAINN 代表',
    author_avatar: 'https://readdy.ai/api/search-image?query=professional%20japanese%20male%20portrait%2C%20clean%20minimal%20background%2C%20soft%20studio%20lighting%2C%20business%20casual%20attire%2C%20confident%20friendly%20expression%2C%20high%20quality%20headshot%20photography&width=100&height=100&seq=author-goto-01&orientation=squarish',
    reading_time: 5,
    published: false,
    published_at: new Date().toISOString().split('T')[0],
  });
  const [tagInput, setTagInput] = useState('');
  const [slugManual, setSlugManual] = useState(false);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    if (post) {
      setForm({ ...post });
      setSlugManual(true);
    }
  }, [post]);

  const handleTitleChange = (title: string) => {
    setForm((prev) => ({
      ...prev,
      title,
      slug: slugManual ? prev.slug : generateSlug(title),
    }));
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !(form.tags ?? []).includes(tag)) {
      setForm((prev) => ({ ...prev, tags: [...(prev.tags ?? []), tag] }));
    }
    setTagInput('');
  };

  const removeTag = (tag: string) => {
    setForm((prev) => ({ ...prev, tags: (prev.tags ?? []).filter((t) => t !== tag) }));
  };

  const handleSubmit = async (published: boolean) => {
    await onSave({
      ...form,
      published,
      published_at: published ? (form.published_at ?? new Date().toISOString()) : form.published_at,
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-[#E8F0FF] flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={onCancel}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#F5F7FA] text-[#666] hover:bg-[#E8F0FF] hover:text-[#1E6FFF] transition-all cursor-pointer"
          >
            <i className="ri-arrow-left-line text-sm" />
          </button>
          <h2 className="text-base font-bold text-[#0A1628]">
            {post?.id ? '記事を編集' : '新規記事作成'}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPreview(!preview)}
            className={`text-xs px-4 py-2 rounded-lg font-medium transition-all cursor-pointer whitespace-nowrap ${preview ? 'bg-[#E8F0FF] text-[#1E6FFF]' : 'bg-[#F5F7FA] text-[#666] hover:bg-[#E8F0FF]'}`}
          >
            <i className={`ri-${preview ? 'edit' : 'eye'}-line mr-1`} />
            {preview ? '編集に戻る' : 'プレビュー'}
          </button>
          <button
            onClick={() => handleSubmit(false)}
            disabled={saving}
            className="text-xs px-4 py-2 rounded-lg font-medium bg-[#F5F7FA] text-[#666] hover:bg-[#E8F0FF] hover:text-[#1E6FFF] transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            下書き保存
          </button>
          <button
            onClick={() => handleSubmit(true)}
            disabled={saving}
            className="text-xs px-5 py-2 rounded-lg font-bold text-white transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
            style={{ background: 'linear-gradient(90deg, #1E6FFF, #0099FF)' }}
          >
            {saving ? '保存中...' : '公開する'}
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {preview ? (
          <div className="max-w-3xl mx-auto px-6 py-10">
            <h1 className="text-3xl font-bold text-[#0A1628] mb-4">{form.title || 'タイトル未入力'}</h1>
            <p className="text-[#666] text-sm mb-8 leading-relaxed">{form.excerpt}</p>
            <div className="prose text-[#444] text-sm leading-relaxed whitespace-pre-wrap">{form.content}</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] h-full">
            {/* Main */}
            <div className="p-6 space-y-5 border-r border-[#E8F0FF]">
              {/* Title */}
              <div>
                <label className="block text-xs font-semibold text-[#666] mb-1.5 tracking-wide">タイトル *</label>
                <input
                  type="text"
                  value={form.title ?? ''}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  placeholder="記事タイトルを入力..."
                  className="w-full text-lg font-bold text-[#0A1628] border-0 border-b-2 border-[#E8F0FF] focus:border-[#1E6FFF] outline-none pb-2 bg-transparent transition-colors placeholder:text-[#ccc] placeholder:font-normal"
                />
              </div>

              {/* Slug */}
              <div>
                <label className="block text-xs font-semibold text-[#666] mb-1.5 tracking-wide">スラッグ（URL）</label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[#999]">/blog/</span>
                  <input
                    type="text"
                    value={form.slug ?? ''}
                    onChange={(e) => { setSlugManual(true); setForm((p) => ({ ...p, slug: e.target.value })); }}
                    className="flex-1 text-sm text-[#444] border border-[#E8F0FF] rounded-lg px-3 py-2 focus:outline-none focus:border-[#1E6FFF] bg-[#FAFBFF]"
                  />
                </div>
              </div>

              {/* Excerpt */}
              <div>
                <label className="block text-xs font-semibold text-[#666] mb-1.5 tracking-wide">概要文 *</label>
                <textarea
                  value={form.excerpt ?? ''}
                  onChange={(e) => setForm((p) => ({ ...p, excerpt: e.target.value }))}
                  placeholder="記事の概要を入力（一覧ページに表示されます）"
                  rows={3}
                  maxLength={500}
                  className="w-full text-sm text-[#444] border border-[#E8F0FF] rounded-xl px-4 py-3 focus:outline-none focus:border-[#1E6FFF] bg-[#FAFBFF] resize-none placeholder:text-[#ccc]"
                />
                <p className="text-xs text-[#bbb] text-right mt-1">{(form.excerpt ?? '').length}/500</p>
              </div>

              {/* Content */}
              <div>
                <label className="block text-xs font-semibold text-[#666] mb-1.5 tracking-wide">本文 *</label>
                <p className="text-xs text-[#bbb] mb-2">Markdown形式で記述できます（## 見出し、### 小見出し、- リスト、**太字**）</p>
                <textarea
                  value={form.content ?? ''}
                  onChange={(e) => setForm((p) => ({ ...p, content: e.target.value }))}
                  placeholder="## はじめに&#10;&#10;記事の本文をここに書いてください..."
                  rows={20}
                  className="w-full text-sm text-[#444] border border-[#E8F0FF] rounded-xl px-4 py-3 focus:outline-none focus:border-[#1E6FFF] bg-[#FAFBFF] resize-none font-mono placeholder:text-[#ccc] placeholder:font-sans"
                />
              </div>
            </div>

            {/* Sidebar */}
            <div className="p-6 space-y-6 bg-[#FAFBFF]">
              {/* Category */}
              <div>
                <label className="block text-xs font-semibold text-[#666] mb-2 tracking-wide">カテゴリ</label>
                <div className="grid grid-cols-1 gap-1.5">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setForm((p) => ({ ...p, category: cat.id }))}
                      className={`text-left text-xs px-3 py-2 rounded-lg font-medium transition-all cursor-pointer whitespace-nowrap ${
                        form.category === cat.id
                          ? 'text-white'
                          : 'bg-white text-[#666] border border-[#E8F0FF] hover:border-[#1E6FFF] hover:text-[#1E6FFF]'
                      }`}
                      style={form.category === cat.id ? { background: 'linear-gradient(90deg, #1E6FFF, #0099FF)' } : {}}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-xs font-semibold text-[#666] mb-2 tracking-wide">タグ</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                    placeholder="タグを入力してEnter"
                    className="flex-1 text-xs border border-[#E8F0FF] rounded-lg px-3 py-2 focus:outline-none focus:border-[#1E6FFF] bg-white"
                  />
                  <button
                    onClick={addTag}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#E8F0FF] text-[#1E6FFF] hover:bg-[#1E6FFF] hover:text-white transition-all cursor-pointer flex-shrink-0"
                  >
                    <i className="ri-add-line text-sm" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(form.tags ?? []).map((tag) => (
                    <span key={tag} className="flex items-center gap-1 text-xs bg-white border border-[#E8F0FF] text-[#666] px-2 py-1 rounded-full">
                      #{tag}
                      <button onClick={() => removeTag(tag)} className="text-[#bbb] hover:text-red-400 transition-colors cursor-pointer">
                        <i className="ri-close-line text-xs" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Cover Image */}
              <div>
                <label className="block text-xs font-semibold text-[#666] mb-2 tracking-wide">カバー画像URL</label>
                <input
                  type="text"
                  value={form.cover_image ?? ''}
                  onChange={(e) => setForm((p) => ({ ...p, cover_image: e.target.value }))}
                  placeholder="https://..."
                  className="w-full text-xs border border-[#E8F0FF] rounded-lg px-3 py-2 focus:outline-none focus:border-[#1E6FFF] bg-white"
                />
                {form.cover_image && (
                  <div className="mt-2 rounded-lg overflow-hidden h-24 bg-[#F5F7FA]">
                    <img src={form.cover_image} alt="cover" className="w-full h-full object-cover object-top" />
                  </div>
                )}
              </div>

              {/* Reading Time */}
              <div>
                <label className="block text-xs font-semibold text-[#666] mb-2 tracking-wide">読了時間（分）</label>
                <input
                  type="number"
                  min={1}
                  max={60}
                  value={form.reading_time ?? 5}
                  onChange={(e) => setForm((p) => ({ ...p, reading_time: Number(e.target.value) }))}
                  className="w-full text-sm border border-[#E8F0FF] rounded-lg px-3 py-2 focus:outline-none focus:border-[#1E6FFF] bg-white"
                />
              </div>

              {/* Published At */}
              <div>
                <label className="block text-xs font-semibold text-[#666] mb-2 tracking-wide">公開日</label>
                <input
                  type="date"
                  value={form.published_at ? form.published_at.split('T')[0] : ''}
                  onChange={(e) => setForm((p) => ({ ...p, published_at: e.target.value }))}
                  className="w-full text-sm border border-[#E8F0FF] rounded-lg px-3 py-2 focus:outline-none focus:border-[#1E6FFF] bg-white"
                />
              </div>

              {/* Author */}
              <div>
                <label className="block text-xs font-semibold text-[#666] mb-2 tracking-wide">著者名</label>
                <input
                  type="text"
                  value={form.author_name ?? ''}
                  onChange={(e) => setForm((p) => ({ ...p, author_name: e.target.value }))}
                  className="w-full text-sm border border-[#E8F0FF] rounded-lg px-3 py-2 focus:outline-none focus:border-[#1E6FFF] bg-white mb-2"
                />
                <label className="block text-xs font-semibold text-[#666] mb-2 tracking-wide">著者役職</label>
                <input
                  type="text"
                  value={form.author_role ?? ''}
                  onChange={(e) => setForm((p) => ({ ...p, author_role: e.target.value }))}
                  className="w-full text-sm border border-[#E8F0FF] rounded-lg px-3 py-2 focus:outline-none focus:border-[#1E6FFF] bg-white"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
